"use client";
import React, { useEffect, useState } from "react";
import { MoveLeft, MoveRight } from "lucide-react";
import Image from "next/image";
import { getImageUrl } from "@/lib/utils/image";

// Lazy load Swiper modules
let Swiper = null;
let SwiperSlide = null;
let Navigation = null;
let Pagination = null;
let Mousewheel = null;
let Keyboard = null;

export default function SingleProductSlider({ images = [], onSwiper, onSlideChange }) {
  const [swiperLoaded, setSwiperLoaded] = useState(false);
  const productImages = Array.isArray(images) && images.length > 0 
    ? images 
    : [{ url: "/image/brand/smart.jpg" }];

  useEffect(() => {
    // Lazy load Swiper and its modules
    Promise.all([
      import("swiper/react"),
      import("swiper/modules"),
      import("swiper/css"),
      import("swiper/css/navigation"),
      import("swiper/css/pagination")
    ]).then(([swiperReact, swiperModules]) => {
      Swiper = swiperReact.Swiper;
      SwiperSlide = swiperReact.SwiperSlide;
      Navigation = swiperModules.Navigation;
      Pagination = swiperModules.Pagination;
      Mousewheel = swiperModules.Mousewheel;
      Keyboard = swiperModules.Keyboard;
      setSwiperLoaded(true);
    }).catch(err => {
      console.error("Failed to load Swiper:", err);
    });
  }, []);

  // Fallback before Swiper loads - show first image
  if (!swiperLoaded || !Swiper) {
    const firstImage = productImages[0];
    const imageUrl = getImageUrl(firstImage.url || firstImage);

    return (
      <div className="relative w-full flex items-center justify-center overflow-hidden glass-box lg:w-[440px] shrink-0">
        <div className="w-full h-full flex items-center p-8 lg:p-12 justify-center">
          <Image 
            src={imageUrl} 
            alt={firstImage.title || "Product image"}
            width={400}
            height={400}
            className="object-contain w-[245px] h-[245px] lg:w-[345px] lg:h-[345px]"
            priority
            fetchPriority="high"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full flex items-center justify-center overflow-hidden glass-box lg:w-[440px] shrink-0">
      
      <button className="swiper-prev-btn absolute left-5 top-1/2 -translate-y-1/2 z-40 h-14 w-14 rounded-full border border-white/20 flex items-center justify-center transition-all active:border-2 active:border-white hover:bg-white/10 group">
        <MoveLeft className="text-white opacity-50 group-hover:opacity-100" size={28} />
      </button>

      <button className="swiper-next-btn absolute right-5 top-1/2 -translate-y-1/2 z-40 h-14 w-14 rounded-full border border-white/20 flex items-center justify-center transition-all active:border-2 active:border-white hover:bg-white/10 group">
        <MoveRight className="text-white opacity-50 group-hover:opacity-100" size={28} />
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-40">
        <div className="custom-pagination flex gap-3 h-2 items-center" />
      </div>
   
      <Swiper
        onSwiper={onSwiper}
        onSlideChange={(s) => onSlideChange && onSlideChange(s)}
        modules={[Navigation, Pagination, Mousewheel, Keyboard]}
        loop={productImages.length > 1} 
        cssMode={false} 
        mousewheel={true}
        keyboard={true}
        navigation={{
          prevEl: ".swiper-prev-btn",
          nextEl: ".swiper-next-btn",
        }}
        pagination={{
          el: ".custom-pagination",
          clickable: true,
          bulletClass: "swiper-pagination-bullet !m-0 !w-6 !h-full !bg-white !opacity-20 !transition-all !duration-300 !rounded-full",
          bulletActiveClass: "!w-12 !opacity-100",
        }}
        className="max-w-[500px] w-full h-full"
      >
        {productImages.map((image, index) => {
          const imageUrl = getImageUrl(image.url || image);
          
          return (
            <SwiperSlide key={image.id || index}>
              <div className="w-full h-full flex items-center p-8 lg:p-12 justify-center">
                <Image 
                  src={imageUrl} 
                  alt={image.title || `Product image ${index + 1}`}
                  width={400}
                  height={400}
                  className="object-contain w-[245px] h-[245px] lg:w-[345px] lg:h-[345px]"
                  {...(index === 0
                    ? { priority: true, fetchPriority: "high" }
                    : { loading: "lazy" })}
                />
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      <style dir="ltr">{`
        .custom-pagination .swiper-pagination-bullet {
          display: block !important;
        }
      `}</style>
    </div>
  );
}